import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactSelect, { StylesConfig } from 'react-select';
import {
  ArrowLeft,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit,
  Eye,
  Globe,
  Loader2,
  Lock,
  Plus,
  Target,
  TrendingUp,
  AlertCircle,
  Zap,
  Route,
  GripVertical,
  X,
  Copy,
  CheckCheck,
  Users,
  Award,
  Sparkles,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { learningPathApiService } from '@/services/api/learning-path-api.service';
import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import EditLearningPathModal from '@/components/learning-paths/EditLearningPathModal';
import DeleteConfirmationModal from '@/components/ui/DeleteConfirmationModal';
import PublishConfirmModal from '@/components/ui/PublishConfirmModal';
import useHasPermission from '@/hooks/useHasPermission';
import { LEARNING_PATH_DELETE_PERMISSION } from '@/constants/constant-permissions';

// Custom styles for react-select with purple theme
const customSelectStyles: StylesConfig<{ label: string; value: string }, false> = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#a855f7' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(168, 85, 247, 0.1)' : 'none',
    '&:hover': {
      borderColor: '#a855f7',
    },
    minHeight: '42px',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#a855f7'
      : state.isFocused
      ? '#fae8ff'
      : 'white',
    color: state.isSelected ? 'white' : '#1f2937',
    cursor: 'pointer',
    fontSize: '0.875rem',
    '&:active': {
      backgroundColor: '#9333ea',
    },
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 50,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '0.25rem',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9ca3af',
    fontSize: '0.875rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#1f2937',
    fontSize: '0.875rem',
  }),
};

interface CourseInPath {
  course_id: string;
  order: number;
  course_title: string;
  course_description?: string;
  difficulty_level?: string;
  rationale?: string;
}

interface SortableCourseItemProps {
  course: CourseInPath;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  /** Published paths are frozen: no reorder, no removing courses. */
  readOnly?: boolean;
}

function SortableCourseItem({ course, index, isExpanded, onToggle, onRemove, readOnly = false }: SortableCourseItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: course.course_id,
    disabled: readOnly,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty?.toLowerCase()) {
      case 'beginner':
      case 'easy':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'intermediate':
      case 'medium':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'advanced':
      case 'hard':
        return 'bg-rose-50 text-rose-700 border-rose-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-gray-200 rounded-xl hover:shadow-md transition-all"
    >
      <div
        className="p-4 cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-start gap-3">
          <div
            {...(readOnly ? {} : attributes)}
            {...(readOnly ? {} : listeners)}
            className={`flex-shrink-0 mt-1 ${readOnly ? 'cursor-not-allowed opacity-40' : 'cursor-grab active:cursor-grabbing'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-5 h-5 text-gray-400" />
          </div>

          <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-bold">
            {index + 1}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{course.course_title}</h3>
                {course.difficulty_level && (
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full border ${getDifficultyColor(course.difficulty_level)}`}>
                    {course.difficulty_level}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove();
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Remove course"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-100">
          {course.course_description && (
            <p className="text-sm text-gray-600 mb-2 mt-3">{course.course_description}</p>
          )}
          {course.rationale && (
            <div className="mt-3 p-3 bg-purple-50 border border-purple-100 rounded-lg">
              <p className="text-xs text-purple-700 flex items-start gap-2">
                <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span><strong>AI Rationale:</strong> {course.rationale}</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EditLearningPath() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [courses, setCourses] = useState<CourseInPath[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [expandedCourses, setExpandedCourses] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<CourseInPath | null>(null);
  const [copiedSlug, setCopiedSlug] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Scroll to top on mount / ID change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showStatusDropdown]);

  // Fetch learning path details
  const { data: learningPath, isLoading } = useQuery({
    queryKey: ['learning-path-details', id],
    queryFn: () => learningPathApiService.getLearningPathById(id!),
    enabled: !!id,
  });

  // Publishing is one-way: a published path is live and frozen — no course
  // add/remove/reorder, no settings edit, and no route back to draft.
  const isPublished = learningPath?.status === 'PUBLISHED';

  // Deleting a learning path is a separate grant from editing it.
  const canDeleteLearningPath = useHasPermission([LEARNING_PATH_DELETE_PERMISSION.ADMIN_LEARNING_PATH_DELETE]);

  // Sync fetched courses with local state
  useEffect(() => {
    if (learningPath?.courses) {
      setCourses(learningPath.courses.sort((a, b) => a.order - b.order));
    }
  }, [learningPath]);

  // Fetch published courses
  const { data: publishedCourses = [] } = useQuery({
    queryKey: ['published-courses'],
    queryFn: () => learningPathApiService.getPublishedCourses(),
  });

  // Update courses mutation
  const updateCoursesMutation = useMutation({
    mutationFn: (updatedCourses: CourseInPath[]) =>
      learningPathApiService.updateLearningPath(id!, {
        courses: updatedCourses.map((c, idx) => ({
          course_id: c.course_id,
          order: idx + 1,
          course_title: c.course_title,
          course_description: c.course_description,
          difficulty_level: c.difficulty_level,
          rationale: c.rationale,
        })),
      }),
    onSuccess: () => {
      toast.success('Courses updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['learning-path-details', id] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update courses');
    },
  });

  // Status update mutation
  const statusMutation = useMutation({
    mutationFn: (status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED') =>
      learningPathApiService.updateLearningPathStatus(id!, status),
    onSuccess: (data) => {
      const statusLabels = {
        DRAFT: 'moved to draft',
        PUBLISHED: 'published',
        ARCHIVED: 'Unpublished'
      };
      toast.success(`Learning path ${statusLabels[data.status]} successfully!`);
      queryClient.invalidateQueries({ queryKey: ['learning-path-details', id] });
      queryClient.invalidateQueries({ queryKey: ['learning-paths'] });
      setShowStatusDropdown(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update learning path status');
    },
  });

  // Drag end handler
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (isPublished) return;

    if (over && active.id !== over.id) {
      setCourses((items) => {
        const oldIndex = items.findIndex((item) => item.course_id === active.id);
        const newIndex = items.findIndex((item) => item.course_id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex);

        // Auto-save after reordering
        updateCoursesMutation.mutate(newOrder);

        return newOrder;
      });
    }
  };

  const handleAddCourse = () => {
    if (!selectedCourseId) return;
    if (isPublished) {
      toast.error('This learning path is published and can no longer be edited.');
      return;
    }

    const course = publishedCourses.find((c) => c._id === selectedCourseId);
    if (!course) return;

    if (courses.find((c) => c.course_id === selectedCourseId)) {
      toast.error('This course is already in the learning path');
      return;
    }

    const newCourse: CourseInPath = {
      course_id: course._id,
      order: courses.length + 1,
      course_title: course.title,
      course_description: course.course_description,
      difficulty_level: course.difficulty_level,
      rationale: '',
    };

    const updatedCourses = [...courses, newCourse];
    setCourses(updatedCourses);
    updateCoursesMutation.mutate(updatedCourses);
    setSelectedCourseId('');
  };

  const handleRemoveCourse = (course: CourseInPath) => {
    setCourseToDelete(course);
    setShowDeleteModal(true);
  };

  const confirmRemoveCourse = () => {
    if (!courseToDelete) return;
    if (isPublished) {
      toast.error('This learning path is published and can no longer be edited.');
      return;
    }

    const updatedCourses = courses.filter((c) => c.course_id !== courseToDelete.course_id);
    setCourses(updatedCourses);
    updateCoursesMutation.mutate(updatedCourses);
    setShowDeleteModal(false);
    setCourseToDelete(null);
  };

  const toggleCourse = (courseId: string) => {
    const newExpanded = new Set(expandedCourses);
    if (newExpanded.has(courseId)) {
      newExpanded.delete(courseId);
    } else {
      newExpanded.add(courseId);
    }
    setExpandedCourses(newExpanded);
  };

  const handleExpandAll = () => {
    if (expandAll) {
      setExpandedCourses(new Set());
      setExpandAll(false);
    } else {
      const allCourseIds = new Set(courses.map((c) => c.course_id));
      setExpandedCourses(allCourseIds);
      setExpandAll(true);
    }
  };

  const handleCopySlug = async () => {
    if (learningPath?.slug) {
      try {
        await navigator.clipboard.writeText(learningPath.slug);
        setCopiedSlug(true);
        toast.success('Slug copied to clipboard!');
        setTimeout(() => setCopiedSlug(false), 2000);
      } catch (err) {
        toast.error('Failed to copy slug');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          <SkeletonLoader type="title" />
          <SkeletonLoader type="text" count={5} />
          <SkeletonLoader type="text" count={5} />
        </div>
      </div>
    );
  }

  if (!learningPath) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
            <AlertCircle className="w-10 h-10 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Learning Path Not Found</h2>
          <p className="text-gray-600 mb-6">
            The learning path you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/admin/learning-paths"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Learning Paths
          </Link>
        </div>
      </div>
    );
  }

  const availableCourses = publishedCourses.filter(
    (c) => !courses.find((existing) => existing.course_id === c._id)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Back Button */}
        <Link
          to="/admin/learning-paths"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium group"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          Back to Learning Path
        </Link>

        {/* Header Card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Banner or Gradient Background */}
          <div className="relative h-48 bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600">
            {learningPath.banner_image_url ? (
              <img
                src={learningPath.banner_image_url}
                alt="Learning path banner"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <Route className="w-20 h-20 text-white/30" />
              </div>
            )}

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex items-center gap-3">
              <button
                onClick={() => window.open(`/admin/learning-paths/preview/${id}`, '_blank')}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-white/95 backdrop-blur-sm hover:bg-white text-gray-900 shadow-lg hover:shadow-xl transition-all border border-white/20"
                title="Preview Learning Path"
              >
                <Eye className="w-4 h-4" />
                <span className="text-sm">Preview</span>
              </button>
              {/* Still shown when published — settings open read-only, and the
                  delete action inside has its own permission. */}
              <button
                onClick={() => setShowEditModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-white/95 backdrop-blur-sm hover:bg-white text-gray-900 shadow-lg hover:shadow-xl transition-all border border-white/20"
              >
                <Edit className="w-4 h-4" />
                <span className="text-sm">{isPublished ? 'View Settings' : 'Edit Settings'}</span>
              </button>

              {/* Published paths show a locked badge — publishing is final. */}
              {isPublished && (
                <div
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/95 backdrop-blur-sm text-gray-900 rounded-lg border border-white/20 shadow-md font-semibold"
                  title="This learning path is published and can no longer be edited or moved back to draft."
                >
                  <div className="w-2 h-2 rounded-full bg-green-500 shadow-sm mr-1"></div>
                  <span className="text-sm mr-2">Published</span>
                  <Lock className="w-3.5 h-3.5 text-gray-500" />
                </div>
              )}

              {/* Status Dropdown */}
              {!isPublished && (
              <div ref={statusDropdownRef} className="relative">
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={statusMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-white/95 backdrop-blur-sm hover:bg-white text-gray-900 shadow-lg hover:shadow-xl transition-all border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center gap-2">
                    {statusMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                    ) : (
                      <div className={`w-2 h-2 rounded-full ${
                        learningPath.status === 'PUBLISHED'
                          ? 'bg-green-500'
                          : learningPath.status === 'ARCHIVED'
                          ? 'bg-gray-500'
                          : 'bg-yellow-500'
                      } shadow-sm`}></div>
                    )}
                    <span className="text-sm">
                      {learningPath.status === 'PUBLISHED'
                        ? 'Published'
                        : learningPath.status === 'ARCHIVED'
                        ? 'Unpublished'
                        : 'Draft'}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${showStatusDropdown ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {showStatusDropdown && (
                  <>
                    {/* Backdrop */}
                    <div className="fixed inset-0 z-40" onClick={() => setShowStatusDropdown(false)}></div>

                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50 py-1">
                      {/* Draft Option */}
                      <button
                        onClick={() => {
                          setShowStatusDropdown(false);
                          if (learningPath.status !== 'DRAFT') {
                            statusMutation.mutate('DRAFT');
                          }
                        }}
                        disabled={statusMutation.isPending}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2.5 group disabled:opacity-50 disabled:cursor-default"
                      >
                        <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0"></div>
                        <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                          Draft
                        </span>
                        {learningPath.status === 'DRAFT' && (
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        )}
                      </button>

                      {/* Published Option */}
                      <button
                        onClick={() => {
                          setShowStatusDropdown(false);
                          setShowPublishConfirm(true);
                        }}
                        disabled={statusMutation.isPending}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-2.5 group disabled:opacity-50 disabled:cursor-default"
                      >
                        <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
                        <span className="flex-1 text-sm font-medium text-gray-700 group-hover:text-gray-900">
                          Published
                        </span>
                        {learningPath.status === 'PUBLISHED' && (
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Header Content */}
          <div className="p-6 md:p-8">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Thumbnail */}
              {learningPath.thumbnail_image_url && (
                <div className="flex-shrink-0">
                  <img
                    src={learningPath.thumbnail_image_url}
                    alt="Learning path thumbnail"
                    className="w-24 h-24 rounded-lg object-cover shadow-md"
                  />
                </div>
              )}

              {/* Title and Description */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-3 mb-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Route className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                      {learningPath.title}
                    </h1>
                    <p className="text-gray-600 leading-relaxed">
                      {learningPath.description || 'No description available'}
                    </p>
                  </div>
                </div>

                {/* Metadata Section */}
                <div className="space-y-3 mt-4">
                  {/* Slug */}
                  {learningPath.slug && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[60px] pt-1.5">Slug:</span>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 text-purple-700 rounded-lg text-sm font-medium">
                        <Globe className="w-4 h-4 flex-shrink-0" />
                        <span className="font-mono break-all">{learningPath.slug}</span>
                        <button
                          onClick={handleCopySlug}
                          className="ml-1 p-1 hover:bg-purple-100 rounded transition-colors flex-shrink-0"
                          title="Copy slug"
                        >
                          {copiedSlug ? (
                            <CheckCheck className="w-3.5 h-3.5 text-green-600" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {learningPath.tags && learningPath.tags.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="text-sm font-medium text-gray-600 min-w-[60px] pt-1.5">Tags:</span>
                      <div className="flex flex-wrap gap-2">
                        {learningPath.tags.map((tag, index) => (
                          <div
                            key={index}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-sm font-medium shadow-sm"
                          >
                            <Target className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{tag}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Courses Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3 mb-1">
                <Zap className="w-7 h-7 text-yellow-500" />
                Course Sequence
              </h2>
              <p className="text-gray-600">Manage and reorder courses in this learning path</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {courses.length > 0 && (
                <button
                  onClick={handleExpandAll}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  {expandAll ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Collapse All
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Expand All
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Add Course Dropdown — hidden once published (path is frozen). */}
          {!isPublished && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Add Published Course
            </label>
            <div className="flex gap-3">
              <div className="flex-1">
                <ReactSelect
                  value={availableCourses
                    .map(course => ({ label: course.title, value: course._id }))
                    .find(option => option.value === selectedCourseId) || null}
                  onChange={(option) => setSelectedCourseId(option?.value || '')}
                  options={availableCourses.map(course => ({
                    label: course.title,
                    value: course._id
                  }))}
                  styles={customSelectStyles}
                  placeholder="Select a course to add..."
                  isSearchable={true}
                  isClearable={true}
                  classNamePrefix="react-select"
                  className="react-select-container"
                />
              </div>
              <button
                type="button"
                onClick={handleAddCourse}
                disabled={!selectedCourseId || updateCoursesMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-5 h-5" />
                Add Course
              </button>
            </div>
          </div>
          )}

          {/* Course List */}
          {courses.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                <BookOpen className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No courses yet</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Start building your learning path by adding courses. Create a comprehensive learning journey for your learners.
              </p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={courses.map((c) => c.course_id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-4">
                  {courses.map((course, index) => (
                    <SortableCourseItem
                      key={course.course_id}
                      course={course}
                      index={index}
                      isExpanded={expandedCourses.has(course.course_id)}
                      onToggle={() => toggleCourse(course.course_id)}
                      onRemove={() => handleRemoveCourse(course)}
                      readOnly={isPublished}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

      </div>

      {/* Modals */}
      {showEditModal && learningPath && (
        <EditLearningPathModal
          learningPath={learningPath}
          canDelete={canDeleteLearningPath}
          isPublished={isPublished}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            queryClient.invalidateQueries({ queryKey: ['learning-path-details', id] });
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setCourseToDelete(null);
        }}
        onConfirm={confirmRemoveCourse}
        title="Remove Course from Learning Path"
        description="Are you sure you want to remove this course from the learning path? This will not delete the course itself, only remove it from this path."
        itemName={courseToDelete?.course_title}
        isLoading={updateCoursesMutation.isPending}
        confirmButtonText="Remove"
      />

      {/* Publish confirmation — publishing is irreversible. */}
      <PublishConfirmModal
        isOpen={showPublishConfirm}
        onClose={() => setShowPublishConfirm(false)}
        onConfirm={() => { setShowPublishConfirm(false); statusMutation.mutate('PUBLISHED'); }}
        entityLabel="learning path"
        itemName={learningPath?.title}
        isPending={statusMutation.isPending}
        lockedActions={[
          'Add, remove or reorder its courses',
          'Edit its settings',
          'Move it back to draft',
        ]}
      />

    </div>
  );
}
